/**
 * The Numato relay controller-board contract, and the client-side mirror of
 * the checks the server (LiftCloud structure checks + VTS limits) applies to
 * it before accepting a save.
 *
 * Kept as a plain `.ts` module rather than `.tsx` — it renders nothing — so
 * `scripts/wizardscan.mjs`, which seeds its scan from every `.tsx` file under
 * `src/pages/setupWizard`, never looks at it for controls. `CdoNumatoRelayEditor.tsx`
 * imports these instead of defining them, so the contract and its validation
 * live in exactly one place.
 */
import { u128HalfIsValid } from "./numatoU128Fields";

// ************************************************ */
// CONTROLLER CONTRACT ***************************** */
export const MAX_TRIGGER_RELAYS = 16;
export const USB_GPIO_PIN_MAX = 35;
export const HOST_GPIO_LINE_MAX = 255;
export const MAX_BAUD_RATE = 4294967295;

export type ControllerBackend =
  | { kind: "NumatoUsbGpioPins"; port: { path: string; baud_rate: number }; pins: number[] }
  | { kind: "HostGpioLines"; chip: number; lines: number[] };

export interface ControllerBoard {
  backend: ControllerBackend;
  /**
   * True only for a unit wired through an inverting stage, where a relay
   * energizes on a low trigger. False is the factory wiring, and it is also what
   * a CDO without the key means, so an unset value can never invert the board.
   */
  active_low: boolean;
}

// ************************************************ */
// DEFAULT BUILDERS ******************************* */
export function defaultUsbBackend(): ControllerBackend {
  return { kind: "NumatoUsbGpioPins", port: { path: "", baud_rate: 19200 }, pins: [] };
}

export function defaultHostBackend(): ControllerBackend {
  return { kind: "HostGpioLines", chip: 0, lines: [] };
}

export function defaultControllerBoard(): ControllerBoard {
  return { backend: defaultUsbBackend(), active_low: false };
}

export function triggerValues(backend: ControllerBackend): number[] {
  return backend.kind === "NumatoUsbGpioPins" ? backend.pins : backend.lines;
}

export function withTriggerValues(backend: ControllerBackend, values: number[]): ControllerBackend {
  return backend.kind === "NumatoUsbGpioPins"
    ? { ...backend, pins: values }
    : { ...backend, lines: values };
}

/** The highest trigger value this backend's hardware can address. */
export function triggerMax(backend: ControllerBackend): number {
  return backend.kind === "NumatoUsbGpioPins" ? USB_GPIO_PIN_MAX : HOST_GPIO_LINE_MAX;
}

/**
 * The lowest integer in 0..=triggerMax(backend) not already used by one of
 * this backend's existing trigger lines, or undefined when every value in
 * that range is already taken.
 */
export function nextFreeTrigger(backend: ControllerBackend): number | undefined {
  const used = new Set(triggerValues(backend));
  const max = triggerMax(backend);
  return Array.from({ length: max + 1 }, (_, candidate) => candidate).find(
    (candidate) => !used.has(candidate),
  );
}

// ************************************************ */
// PARSING (defensive — never throws) ************** */

/** Read a NumatoUsbGpioPins or HostGpioLines backend, or undefined if malformed. */
export function readControllerBackend(raw: unknown): ControllerBackend | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const obj = raw as Record<string, unknown>;
  if (obj.kind === "NumatoUsbGpioPins") {
    if (!obj.port || typeof obj.port !== "object") return undefined;
    const port = obj.port as Record<string, unknown>;
    if (typeof port.path !== "string" || typeof port.baud_rate !== "number") return undefined;
    if (!Array.isArray(obj.pins) || !obj.pins.every((v) => typeof v === "number")) {
      return undefined;
    }
    return {
      kind: "NumatoUsbGpioPins",
      port: { path: port.path, baud_rate: port.baud_rate },
      pins: obj.pins as number[],
    };
  }
  if (obj.kind === "HostGpioLines") {
    if (typeof obj.chip !== "number") return undefined;
    if (!Array.isArray(obj.lines) || !obj.lines.every((v) => typeof v === "number")) {
      return undefined;
    }
    return { kind: "HostGpioLines", chip: obj.chip, lines: obj.lines as number[] };
  }
  return undefined;
}

/**
 * Read a ControllerBoard from an arbitrary value, returning undefined for
 * anything that is not a well-formed contract value. Deliberately defensive
 * at every step — a legacy or hand-edited CDO with a malformed `controller`
 * key must hydrate as a plain relay board rather than throw.
 */
export function readController(raw: unknown): ControllerBoard | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const obj = raw as Record<string, unknown>;
  const backend = readControllerBackend(obj.backend);
  if (!backend) return undefined;
  // An absent active_low is the schema default, the factory wiring. Only a
  // value of the wrong type makes the controller malformed.
  if (obj.active_low === undefined) return { backend, active_low: false };
  if (typeof obj.active_low !== "boolean") return undefined;
  return { backend, active_low: obj.active_low };
}

// ************************************************ */
// SERVER-REJECTION MIRROR ************************* */

/** "RL0", "RL0 and RL1", or "RL0, RL1 and RL2" — never an Oxford comma. */
function relayNames(indices: number[]): string {
  const names = indices.map((i) => `RL${i}`);
  if (names.length <= 1) return names.join("");
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/** "RL0" for a single trigger line, "RL0-RL{n-1}" for more than one. */
function triggerLineRangeLabel(n: number): string {
  return n <= 1 ? "RL0" : `RL0-RL${n - 1}`;
}

/** "1 trigger line (RL0)" or "3 trigger lines (RL0-RL2)" — N1. */
function triggerLineCountLabel(n: number): string {
  const noun = n === 1 ? "trigger line" : "trigger lines";
  return `${n} ${noun} (${triggerLineRangeLabel(n)})`;
}

/**
 * Every reason the server would reject this controller, as operator-facing
 * sentences, checked in the order LiftCloud's structure checks and VTS's
 * limits would hit them: no trigger lines; more than 16; each out-of-range or
 * non-integer trigger value, naming the relay; each duplicated trigger value,
 * naming every relay that shares it; then the fields specific to the chosen
 * backend. [] means the controller is saveable as-is.
 */
export function controllerProblems(controller: ControllerBoard): string[] {
  const { backend } = controller;
  const values = triggerValues(backend);
  const max = triggerMax(backend);
  const problems: string[] = [];

  if (values.length === 0) {
    problems.push("The controller board needs at least one trigger relay line.");
  }
  if (values.length > MAX_TRIGGER_RELAYS) {
    problems.push(
      `The controller board has ${values.length} trigger relay lines; the maximum is ${MAX_TRIGGER_RELAYS}.`,
    );
  }

  values.forEach((value, idx) => {
    if (!Number.isInteger(value)) {
      problems.push(`RL${idx} uses ${value}, which must be a whole number.`);
    } else if (value < 0 || value > max) {
      problems.push(`RL${idx} uses ${value}, outside 0-${max}.`);
    }
  });

  const indicesByValue = new Map<number, number[]>();
  values.forEach((value, idx) => {
    indicesByValue.set(value, [...(indicesByValue.get(value) ?? []), idx]);
  });
  indicesByValue.forEach((indices, value) => {
    if (indices.length > 1) {
      problems.push(`${relayNames(indices)} share the trigger value ${value}.`);
    }
  });

  if (backend.kind === "NumatoUsbGpioPins") {
    // Empty and whitespace-padded are reported as separate problems, and
    // never silently trimmed: LiftCloud itself rejects a path that differs
    // from its trimmed form with "must not start or end with whitespace", so
    // trimming here would hide exactly the input the server would 400 on.
    if (!backend.port.path.trim()) {
      problems.push("The serial port path cannot be empty.");
    } else if (backend.port.path !== backend.port.path.trim()) {
      problems.push("The serial port path must not start or end with spaces.");
    }
    if (
      !Number.isInteger(backend.port.baud_rate) ||
      backend.port.baud_rate < 1 ||
      backend.port.baud_rate > MAX_BAUD_RATE
    ) {
      problems.push(`The baud rate must be a whole number from 1 to ${MAX_BAUD_RATE}.`);
    }
  } else if (
    !Number.isInteger(backend.chip) ||
    backend.chip < 0 ||
    backend.chip > HOST_GPIO_LINE_MAX
  ) {
    problems.push(`The GPIO chip number must be a whole number from 0 to ${HOST_GPIO_LINE_MAX}.`);
  }

  return problems;
}

// ************************************************ */
// MAPPING-TABLE COMMANDS *************************** */

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === "object" ? (value as Record<string, unknown>) : undefined;
}

/**
 * Display labels for the outer command kind — mirrors CdoNumatoRelayEditor's
 * COMMAND_CATEGORIES Select options, so a rejection sentence names a command
 * the way the editor itself does.
 *
 * N4: a `Map`, not a plain object — a plain-object lookup keyed on untrusted
 * input (`kind` comes straight off a parsed CDO) resolves an inherited
 * `Object.prototype` member for a key like `"toString"`, which is truthy and
 * so wins over the `?? kind` fallback below, printing the function itself
 * into the sentence instead of falling back to the raw kind string. A `Map`
 * has no prototype chain of string-keyed properties to collide with.
 */
const COMMAND_CATEGORY_LABELS: ReadonlyMap<string, string> = new Map([
  ["InfoMessage", "Info Message"],
  ["IdCommandContentTable", "ID Command"],
  ["GpioCommandContentTable", "GPIO Command"],
  ["VersionMessage", "Version Message"],
  ["RelayCommandContentTable", "Relay Command"],
  ["AdcCommandContentTable", "ADC Command"],
]);

/**
 * Every reason the server would reject a single mapping-table command, as an
 * operator-facing sentence naming the table and 1-based row. [] when the
 * command is one a controller board with `n` trigger lines can serve: a
 * relay On/Off/Read with an in-range index, ReadAll, or a WriteAll (narrow or
 * wide) whose mask fits in `n` bits. Never throws on malformed input.
 *
 * A RelayWriteAllMessage whose U128 half is not itself a valid half (see
 * `u128HalfIsValid`) is left unflagged here — `numatoU128Problems` already
 * reports that, and this must not report it a second time.
 */
function relayCommandProblems(cmd: unknown, table: string, row: number, n: number): string[] {
  const label = `${table} mapping #${row}`;
  const cmdRecord = asRecord(cmd);
  const kind = cmdRecord?.kind;
  if (kind !== "RelayCommandContentTable") {
    const kindLabel =
      typeof kind === "string"
        ? (COMMAND_CATEGORY_LABELS.get(kind) ?? kind)
        : "an unrecognized command";
    return [
      `${label} uses ${kindLabel}, which a controller board cannot answer: it only switches relays.`,
    ];
  }
  const content = asRecord(cmdRecord?.content);
  if (!content) {
    return [`${label} is missing its relay command details.`];
  }
  switch (content.kind) {
    case "OnMessage":
    case "OffMessage":
    case "ReadMessage": {
      // A missing or null message is relay index 0 — LiftCloud (`Message
      // *int32`) and the editor (`message ?? 0`) both treat it so (N1).
      // Anything present but not a whole number (a string like "2", or a
      // fraction like 1.5) is its own, distinct problem: it is not "out of
      // range", it does not parse as an index at all.
      const message = content.message ?? 0;
      if (typeof message !== "number" || !Number.isInteger(message)) {
        return [`${label} has a malformed relay index.`];
      }
      if (message < 0 || message >= n) {
        return [
          `${label} switches RL${message}, but the controller board has ${triggerLineCountLabel(n)}.`,
        ];
      }
      return [];
    }
    case "ReadAllMessage":
      return [];
    case "WriteAllMessage": {
      // A missing or null message is mask 0 — LiftCloud and the editor both
      // treat it so, the same rule On/Off/Read already apply (N1/N-b).
      // Anything present but not a whole number is still malformed.
      const message = content.message ?? 0;
      const limit = 2 ** n;
      if (
        typeof message !== "number" ||
        !Number.isInteger(message) ||
        message < 0 ||
        message >= limit
      ) {
        if (typeof message === "number" && Number.isInteger(message) && message >= 0) {
          return [
            `${label} writes mask 0x${message.toString(16).toUpperCase()}, wider than the controller board's ${n} trigger lines.`,
          ];
        }
        return [`${label} has a malformed Write All mask.`];
      }
      return [];
    }
    case "RelayWriteAllMessage": {
      const value = asRecord(content.value);
      const high = value?.high;
      const low = value?.low;
      if (
        typeof high !== "string" ||
        typeof low !== "string" ||
        !u128HalfIsValid(high) ||
        !u128HalfIsValid(low)
      ) {
        // Reported once, by numatoU128Problems — not duplicated here.
        return [];
      }
      const highBig = BigInt(high);
      const lowBig = BigInt(low);
      if (highBig !== 0n || lowBig >= 2n ** BigInt(n)) {
        const combined = highBig * 2n ** 64n + lowBig;
        return [
          `${label} writes mask 0x${combined.toString(16).toUpperCase()}, wider than the controller board's ${n} trigger lines.`,
        ];
      }
      return [];
    }
    default:
      return [`${label} uses an unrecognized relay command.`];
  }
}

/**
 * Every reason the server would reject the `controller` key of a parsed CDO,
 * and — when the controller is readable and has at least one trigger line —
 * every mapping-table command a controller board with that many trigger
 * lines cannot serve.
 *
 * [] when the parsed CDO carries no `controller` key at all — a plain relay
 * board is always saveable. When the key is present but does not parse as a
 * well-formed ControllerBoard (readController rejects it), a single sentence
 * flagging that the saved value cannot be read. Otherwise, controllerProblems
 * for the parsed board, followed by a problem for every `response_to_message`
 * and `message_to_cmd` command that board cannot serve — skipped entirely
 * when the board has no trigger lines, since controllerProblems already
 * blocks the save on that alone.
 */
export function numatoRelayControllerProblems(parsedCdo: unknown): string[] {
  if (!parsedCdo || typeof parsedCdo !== "object" || !("controller" in parsedCdo)) {
    return [];
  }
  const controller = readController((parsedCdo as { controller?: unknown }).controller);
  if (!controller) {
    return [
      "The saved controller board configuration could not be read. Choose a board type to replace it.",
    ];
  }
  const problems = controllerProblems(controller);
  const n = triggerValues(controller.backend).length;
  if (n > 0) {
    const cdoRecord = parsedCdo as Record<string, unknown>;
    const responseToMessage = Array.isArray(cdoRecord.response_to_message)
      ? cdoRecord.response_to_message
      : [];
    responseToMessage.forEach((entry, idx) => {
      const key = asRecord(asRecord(entry)?.key);
      problems.push(...relayCommandProblems(key?.command, "Response to Message", idx + 1, n));
    });
    const messageToCmd = Array.isArray(cdoRecord.message_to_cmd) ? cdoRecord.message_to_cmd : [];
    messageToCmd.forEach((entry, idx) => {
      const value = asRecord(entry)?.value;
      problems.push(...relayCommandProblems(value, "Message to Command", idx + 1, n));
    });
  }
  return problems;
}
